import { createContext, useEffect, useMemo, useState } from 'react';
import { Store, User, Franchise, System, CurrentUserResponse } from '../../app/services/api/generated';
import { Role } from '../../enums/Role';
import { useUser, useClerk } from '@clerk/clerk-react';
import { disableDemoMode, isDemoModeEnabled, isDemoOnlyPermission } from '../../utils/featureAccess';

export interface UserType extends User {
  username?: string;
  roles?: string[];
}

interface UserContextType {
  user: UserType | null | undefined;

  selectedStore?: Store | null;
  selectedFranchise?: Franchise | null;
  selectedSystem?: System | null;

  stores: Store[],
  franchises: Franchise[],
  systems: System[],

  userFranchisesCurrentTenant: Franchise[];
  userStoresCurrentTenant: Store[];

  setUserContext: (user: CurrentUserResponse) => void;

  forceLogout: () => Promise<void>;

  hasPermissions: () => boolean;
  hasPermissionTo: (action: Role[]) => boolean;
  setUserPermissions: (permissions: string[]) => void;
  isAdmin: () => boolean;
  isOrderlyAdmin: () => boolean;

  setSystems: (systems: System[]) => void;
  setFranchises: (stores: Franchise[]) => void;
  setStores: (stores: Store[]) => void;

  setSelectedStore: (store: Store | null) => void;
  setSelectedFranchise: (franchise: Franchise | null) => void;
  setSelectedSystem: (system: System | null) => void;

  hasPremiumModuleAccess: (moduleName: string) => boolean;
}

const initialContextState: UserContextType = {
  user: null,

  selectedStore: null,
  selectedFranchise: null,
  selectedSystem: null,

  stores: [],
  franchises: [],
  systems: [],

  userFranchisesCurrentTenant: [],
  userStoresCurrentTenant: [],

  setUserContext: (user: CurrentUserResponse) => { },

  forceLogout: () => Promise.resolve(),

  hasPermissions: () => false,
  hasPermissionTo: (action: Role[]) => false,
  setUserPermissions: (permissions: string[]) => { },
  isAdmin: () => false,
  isOrderlyAdmin: () => false,

  setSystems: (systems: System[]) => { },
  setFranchises: (stores: Franchise[]) => { },
  setStores: (stores: Store[]) => { },

  setSelectedStore: (store: Store | null) => { },
  setSelectedFranchise: (franchise: Franchise | null) => { },
  setSelectedSystem: (system: System | null) => { },

  hasPremiumModuleAccess: (moduleName: string) => false,
};

export const UserContext = createContext<UserContextType>(initialContextState);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserContextProvider = ({ children }: any) => {
  const [user, setUser] = useState<UserType | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? (JSON.parse(storedUser)) : null;
  });

  const [selectedStore, setSelectedStore] = useState<Store | null>(() => {
    const storedStore = localStorage.getItem('selectedStore');
    return storedStore ? JSON.parse(storedStore) : null;
  });

  const [selectedFranchise, setSelectedFranchise] = useState<Franchise | null>(() => {
    const storedFranchise = localStorage.getItem('selectedFranchise');
    return storedFranchise ? JSON.parse(storedFranchise) : null;
  });

  const [selectedSystem, setSelectedSystem] = useState<System | null>(() => {
    const storedSystem = localStorage.getItem('selectedSystem');
    return storedSystem ? JSON.parse(storedSystem) : null;
  });

  const [stores, setStores] = useState<Store[]>(() => {
    const storedStores = localStorage.getItem('stores');
    return storedStores ? JSON.parse(storedStores) : [];
  });

  const [franchises, setFranchises] = useState<Franchise[]>(() => {
    const storedFranchises = localStorage.getItem('franchises');
    return storedFranchises ? JSON.parse(storedFranchises) : [];
  });

  const [systems, setSystems] = useState<System[]>(() => {
    const storedSystems = localStorage.getItem('systems');
    return storedSystems ? JSON.parse(storedSystems) : [];
  });

  const [userPermissions, setUserPermissions] = useState<string[]>(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    return storedPermissions ? JSON.parse(storedPermissions) : [];
  });

  const { isSignedIn, isLoaded } = useUser();
  const { signOut } = useClerk();

  useEffect(() => {
    if (!isSignedIn && isLoaded) {
      setUser(null);
      setSelectedStore(null);
      setSelectedFranchise(null);
      setSelectedSystem(null);
      setStores([]);
      setFranchises([]);
      setSystems([]);
      setUserPermissions([]);

      const language = localStorage.getItem('language');
      localStorage.clear();
      if (language) {
        localStorage.setItem('language', language);
        localStorage.setItem('i18nextLng', language);
      }
    }
  }, [isSignedIn]);

  useEffect(() => {
    localStorage.setItem('selectedStore', JSON.stringify(selectedStore));
  }, [selectedStore]);

  useEffect(() => {
    localStorage.setItem('selectedFranchise', JSON.stringify(selectedFranchise));
  }, [selectedFranchise]);

  useEffect(() => {
    localStorage.setItem('selectedSystem', JSON.stringify(selectedSystem));
  }, [selectedSystem]);

  useEffect(() => {
    localStorage.setItem('stores', JSON.stringify(stores));
  }, [stores]);

  useEffect(() => {
    localStorage.setItem('franchises', JSON.stringify(franchises));
  }, [franchises]);

  useEffect(() => {
    localStorage.setItem('systems', JSON.stringify(systems));
  }, [systems]);

  useEffect(() => {
    localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
  }, [userPermissions]);

  const contextValue = useMemo(() => {

    const hasPermissions = () => {
      if ((user?.roles?.length || 0) === 0) return false;

      return userPermissions?.length > 0;
    };

    const setUserContext = (userData: CurrentUserResponse | null) => {
      if (userData == null) return;
      const user: UserType = {
        ...userData,
        username: userData?.emailAddress ?? '',
        roles: userData?.permissions ?? [],
        stores: userData?.stores as Store[] ?? [],
      } as UserType;
      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
    };

    const hasPermissionTo = (actions: Role[]): boolean => {
      if (!user?.roles) return false;

      if (actions.some(a => a === 'User') && (user?.roles.length > 0)) {
        return true;
      }

      // Demo Mode bypasses permission checks for demo-only domains (see featureAccess.ts) —
      // this covers internal page-level checks (buttons/tabs/detail views), complementing the
      // route/nav-level `status: 'demo'` bypass in AuthGuard/useDrawer/Settings.
      if (isDemoModeEnabled() && actions.some(isDemoOnlyPermission)) {
        return true;
      }

      return actions.some(a => userPermissions?.map(r => r?.toLowerCase() ?? '').includes(a?.toLowerCase() ?? 'MISSING'));
    };

    const isAdmin = () => {
      if (!user?.roles) return false;
      return userPermissions.includes('admin');
    };

    const isOrderlyAdmin = () => {
      return user?.userLevel == 'Admin';
    };

    const setUserStores = (stores: Store[]) => {
      if (user == null) return;
      setUser({ ...user, stores });
      localStorage.setItem('user', JSON.stringify({ ...user, stores }));
    };

    const forceLogout = async () => {
      disableDemoMode();
      // await sign-out before navigating, else the page reloads mid-logout and Clerk rehydrates a new session
      await signOut();

      if (window.location.pathname !== '/') {
        window.location.assign('/');
      }
    };

    const hasPremiumModuleAccess = (moduleName: string): boolean => {
      const normalizedModuleName = moduleName.toLowerCase();

      if (selectedStore?.premiumModules?.some((module) => module.name?.toLowerCase() === normalizedModuleName)) {
        return true;
      }

      const selectedSystemModules = selectedSystem?.modules ?? [];
      if (selectedSystemModules.some((module) => module.premium && module.name?.toLowerCase() === normalizedModuleName)) {
        return true;
      }

      return systems.some((system) =>
        (system.modules ?? []).some((module) => module.premium && module.name?.toLowerCase() === normalizedModuleName),
      );
    };

    const userFranchisesCurrentTenant = franchises
      .filter(f => f.tenantId === selectedFranchise?.tenantId && f.franchiseId !== undefined);

    const userStoresCurrentTenant = stores
      .filter(s => userFranchisesCurrentTenant.some(f => f.franchiseId === s.franchiseId));

    return {
      user,

      selectedStore,
      selectedFranchise,
      selectedSystem,

      stores,
      franchises,
      systems,

      userFranchisesCurrentTenant,
      userStoresCurrentTenant,

      setUserContext,

      forceLogout,

      hasPermissions,
      hasPermissionTo,
      setUserPermissions,
      isAdmin,
      isOrderlyAdmin,

      setSystems,
      setFranchises,
      setStores,
      setUserStores,

      setSelectedStore,
      setSelectedFranchise,
      setSelectedSystem,

      hasPremiumModuleAccess,
    };
  }, [user, selectedStore, selectedFranchise, selectedSystem, stores, franchises, systems, userPermissions]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};